import React from "react";
import { WarningBox } from "newui/components/IconBox";
import { PerformanceBadge } from "newui/features/MySuppliers/components/PerformanceBadge";
import {
	getBandForScore,
} from "newui/features/MySuppliers/constants/performance-bands";
import { useRatingWorkflow } from "../../../../context/RatingWorkflowContext";
import type { KpiCategory } from "newui/features/MySuppliers/types/supplier-performance.types";

interface SummaryStepProps {
	categories: KpiCategory[];
}

export const SummaryStep: React.FC<SummaryStepProps> = ({
	categories,
}) => {
	const { state } = useRatingWorkflow();

	const selectedCategories = categories.filter((cat) =>
		state.selectedCategoryIds.includes(cat.id)
	);

	const categoryAverages = selectedCategories.map((category) => {
		const ratings = state.ratings[category.id] ?? [];
		const scores = ratings.filter((r) => r.score > 0).map((r) => r.score);
		const avg =
			scores.length > 0
				? scores.reduce((sum, s) => sum + s, 0) / scores.length
				: 0;

		return {
			categoryId: category.id,
			categoryName: category.name,
			averageScore: avg,
			band: getBandForScore(avg),
			ratedCount: scores.length,
			totalCount: category.sub_criteria.length,
		};
	});

	const allScores = categoryAverages
		.filter((ca) => ca.averageScore > 0)
		.map((ca) => ca.averageScore);
	const overallAverage =
		allScores.length > 0
			? allScores.reduce((sum, s) => sum + s, 0) / allScores.length
			: 0;
	const overallBand = getBandForScore(overallAverage);

	return (
		<div data-testid="summary-step">
			<h3 className="mb-2 text-lg font-semibold text-gray-900">
				Review Summary
			</h3>
			<p className="mb-4 text-sm text-gray-600">
				Review your ratings before submitting. Rating: {state.supplierName}
			</p>

			{/* Overall score */}
			<div
				className="mb-6 flex items-center justify-between rounded-lg bg-gray-50 p-4"
				data-testid="overall-score"
			>
				<div>
					<div className="text-sm font-medium text-gray-500">
						Overall Score
					</div>
					<div className="text-3xl font-bold text-gray-900">
						{overallAverage > 0 ? overallAverage.toFixed(1) : "—"}
					</div>
				</div>
				<PerformanceBadge
					score={overallAverage > 0 ? overallAverage : null}
					band={overallBand}
					size="lg"
				/>
			</div>

			{/* Category breakdown */}
			<div className="mb-6">
				<h4 className="mb-3 text-sm font-semibold text-gray-700">
					Category Scores
				</h4>
				<div className="overflow-hidden rounded-lg border border-gray-200">
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th
									scope="col"
									className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500"
								>
									Category
								</th>
								<th
									scope="col"
									className="px-4 py-2 text-center text-xs font-medium uppercase text-gray-500"
								>
									Score
								</th>
								<th
									scope="col"
									className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500"
								>
									Band
								</th>
								<th
									scope="col"
									className="px-4 py-2 text-center text-xs font-medium uppercase text-gray-500"
								>
									Rated
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-200 bg-white">
							{categoryAverages.map((ca) => (
								<tr
									key={ca.categoryId}
									data-testid={`summary-category-${ca.categoryId}`}
								>
									<td className="px-4 py-2 text-sm text-gray-900">
										{ca.categoryName}
									</td>
									<td className="px-4 py-2 text-center text-sm font-medium text-gray-900">
										{ca.averageScore > 0
											? ca.averageScore.toFixed(1)
											: "—"}
									</td>
									<td className="px-4 py-2">
										<PerformanceBadge
											score={
												ca.averageScore > 0
													? ca.averageScore
													: null
											}
											size="sm"
										/>
									</td>
									<td className="px-4 py-2 text-center text-xs text-gray-500">
										{ca.ratedCount}/{ca.totalCount}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>

			<WarningBox>
				Submitted ratings cannot be edited. Please review all scores
				carefully before submitting.
			</WarningBox>
		</div>
	);
};
